'use client';

import { c as _c } from "react/compiler-runtime";
import { BANK_FUNDING_METHODS, type FundingMethod, type FundingSavedCard, fundingMethodArtwork, isBankFundingMethod, isFundingHostRouteEnabled, isHostedFundingMethod, OnrampApiError, type OnrampClient, onrampManifest } from '@morfi/onramp-sdk';
import { useConnectedExchanges, useConnectedWalletCapability, useCryptoDepositCapability, useFundingNavigation, useSavedFundingMethods } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type MouseEvent, type ReactNode, useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTabs } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { ArrowRight01Icon, Search01Icon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { logger } from '@/lib/telemetry/logger';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingLoading } from '../shared/funding-loading';
import { useFundingBankSubtitles, useFundingMethodLabels } from '../shared/funding-method-presentation';
import { FundingSavedCardRow } from './funding-saved-card-row';
export function FundingMethodPicker(t0) {
  const $ = _c(104);
  const {
    client,
    methods,
    loading: t1,
    directPurchase: t2,
    onSelect,
    onExchange,
    onProfile,
    providerFeedback,
    providerFeedbackOwnsStage: t3,
    feedbackAfterMethods: t4,
    savedCards: t5,
    busy: t6,
    onSelectCard,
    onRemoveCard
  } = t0;
  const loading = t1 === undefined ? false : t1;
  const directPurchase = t2 === undefined ? false : t2;
  const providerFeedbackOwnsStage = t3 === undefined ? false : t3;
  const feedbackAfterMethods = t4 === undefined ? false : t4;
  let t7;
  if ($[0] !== t5) {
    t7 = t5 === undefined ? [] : t5;
    $[0] = t5;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const savedCards = t7;
  const busy = t6 === undefined ? false : t6;
  const t = useTranslations("onramp");
  const bankSubtitles = useFundingBankSubtitles();
  const saved = useSavedFundingMethods(client);
  const exchanges = useConnectedExchanges(client, "web");
  const cryptoDeposit = useCryptoDepositCapability(client, "web");
  const connectedWallet = useConnectedWalletCapability(client, "web");
  const navigation = useFundingNavigation(client);
  const tab = directPurchase ? "money" : navigation.pickerCategory;
  let t8;
  if ($[2] !== t) {
    t8 = t("deposit_title");
    $[2] = t;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  useFundingStage(t8, true, undefined, tab === "wallets" || !providerFeedbackOwnsStage, onProfile);
  const t9 = useFeatureGate("onramp_enabled") === true;
  let t10;
  if ($[4] !== t9) {
    t10 = isFundingHostRouteEnabled(onrampManifest, "manual_transfer", "web", t9, true);
    $[4] = t9;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const manualEnabled = t10;
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase();
  const matches = method => t(method).toLocaleLowerCase().includes(query);
  let t11;
  if ($[6] !== methods) {
    t11 = new Set(methods);
    $[6] = methods;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const selectableMethods = t11;
  let t12;
  if ($[8] !== directPurchase || $[9] !== selectableMethods) {
    t12 = method_0 => method_0 !== "card" && (!directPurchase || selectableMethods.has(method_0));
    $[8] = directPurchase;
    $[9] = selectableMethods;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const savedMethods = (saved.data ?? []).filter(t12);
  const availableMethods = methods.filter(method_1 => !isBankFundingMethod(method_1) && matches(method_1));
  const bankMethods = BANK_FUNDING_METHODS.filter(method_2 => methods.includes(method_2) && (matches(method_2) || t("bank_transfers").toLocaleLowerCase().includes(query)));
  const showBankTransfers = !directPurchase && bankMethods.length > 0;
  let t13;
  let t14;
  if ($[11] !== saved.error || $[12] !== saved.isError) {
    t13 = () => {
      if (saved.isError) {
        logger.warn("onramp.saved_methods_failed", {
          code: saved.error instanceof OnrampApiError ? saved.error.code : "onramp_request_failed"
        });
      }
    };
    t14 = [saved.isError, saved.error];
    $[11] = saved.error;
    $[12] = saved.isError;
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  useEffect(t13, t14);
  const t15 = "flex flex-col gap-4";
  let t16;
  if ($[15] !== busy || $[16] !== onRemoveCard || $[17] !== onSelectCard || $[18] !== savedCards || $[19] !== t) {
    t16 = savedCards.length ? <section className="flex flex-col gap-2" aria-label={t("saved_cards")}><h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t("saved_cards")}</h3><div className="overflow-hidden rounded-control bg-background-tertiary">{savedCards.map(card => <FundingSavedCardRow key={card.id} card={card} busy={busy} onSelect={id => onSelectCard?.(id)} onRemove={id_0 => onRemoveCard?.(id_0)} />)}</div></section> : null;
    $[15] = busy;
    $[16] = onRemoveCard;
    $[17] = onSelectCard;
    $[18] = savedCards;
    $[19] = t;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = savedMethods.length > 0 ? <section className="flex flex-col gap-2" aria-label={t("saved")}><h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t("saved")}</h3><div className="overflow-hidden rounded-control bg-background-tertiary">{savedMethods.map(method_3 => <FundingMethodRow key={method_3} method={method_3} subtitle={selectableMethods.has(method_3) ? t("previously_used") : t("method_unavailable")} onSelect={selectableMethods.has(method_3) ? onSelect : undefined} />)}</div></section> : null;
  const t18 = "flex flex-col gap-3";
  let t19;
  if ($[21] !== t) {
    t19 = t("more_options");
    $[21] = t;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== t) {
    t20 = t("more_options");
    $[23] = t;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t20) {
    t21 = <h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t20}</h3>;
    $[25] = t20;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  const t22 = AppTabs;
  const t23 = "manual";
  let t24;
  if ($[27] !== navigation) {
    t24 = value => navigation.setPickerCategory(value === "wallets" ? "wallets" : "money");
    $[27] = navigation;
    $[28] = t24;
  } else {
    t24 = $[28];
  }
  let t25;
  if ($[29] !== directPurchase || $[30] !== navigation || $[31] !== t) {
    t25 = !directPurchase && <AppTabs.List variant="segmented" size="md"><AppTabs.Trigger value="money" onMouseDown={deferPointerActivation} onClick={() => navigation.setPickerCategory("money")}>{t("add_money")}</AppTabs.Trigger><AppTabs.Trigger value="wallets" onMouseDown={deferPointerActivation} onClick={() => navigation.setPickerCategory("wallets")}>{t("wallets_and_exchanges")}</AppTabs.Trigger></AppTabs.List>;
    $[29] = directPurchase;
    $[30] = navigation;
    $[31] = t;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  const t26 = AppTabs;
  const t27 = "money";
  const t28 = "mt-3 flex flex-col gap-3";
  const t29 = feedbackAfterMethods ? null : providerFeedback;
  let t30;
  if ($[33] !== t) {
    t30 = t("search_methods");
    $[33] = t;
    $[34] = t30;
  } else {
    t30 = $[34];
  }
  let t31;
  if ($[35] !== t) {
    t31 = t("search_methods_placeholder");
    $[35] = t;
    $[36] = t31;
  } else {
    t31 = $[36];
  }
  let t32;
  let t33;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = event => setSearch(event.target.value);
    t33 = <Icon icon={Search01Icon} className="size-icon-md text-foreground-secondary" />;
    $[37] = t32;
    $[38] = t33;
  } else {
    t32 = $[37];
    t33 = $[38];
  }
  let t34;
  if ($[39] !== loading || $[40] !== search || $[41] !== t30 || $[42] !== t31) {
    t34 = <InputField type="search" aria-label={t30} placeholder={t31} value={search} disabled={loading} onChange={t32} leftIcon={t33} className="bg-background-tertiary dark:bg-background-tertiary" />;
    $[39] = loading;
    $[40] = search;
    $[41] = t30;
    $[42] = t31;
    $[43] = t34;
  } else {
    t34 = $[43];
  }
  let t35;
  if ($[44] !== directPurchase || $[45] !== loading) {
    t35 = loading ? <FundingLoading banks={!directPurchase} /> : null;
    $[44] = directPurchase;
    $[45] = loading;
    $[46] = t35;
  } else {
    t35 = $[46];
  }
  const t36 = !loading && availableMethods.length > 0 ? <div className="overflow-hidden rounded-control bg-background-tertiary">{availableMethods.map(method_4 => <FundingMethodRow key={method_4} method={method_4} subtitle={method_4 === "moonpay_balance" ? t("moonpay_balance_subtitle") : isHostedFundingMethod(method_4) ? t("hosted_method_subtitle") : t("instant")} onSelect={onSelect} />)}</div> : null;
  const t37 = !loading && showBankTransfers ? <section className="flex flex-col gap-2" aria-label={t("bank_transfers")}><h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t("bank_transfers")}</h3><div className="overflow-hidden rounded-control bg-background-tertiary">{bankMethods.map(method_5 => <FundingMethodRow key={method_5} method={method_5} subtitle={bankSubtitles[method_5]} onSelect={onSelect} />)}</div></section> : null;
  let t38;
  if ($[47] !== availableMethods || $[48] !== loading || $[49] !== showBankTransfers || $[50] !== t) {
    t38 = !loading && availableMethods.length === 0 && !showBankTransfers ? <p className="text-body text-foreground-secondary">{t("no_matching_methods")}</p> : null;
    $[47] = availableMethods;
    $[48] = loading;
    $[49] = showBankTransfers;
    $[50] = t;
    $[51] = t38;
  } else {
    t38 = $[51];
  }
  const t39 = feedbackAfterMethods ? providerFeedback : null;
  let t40;
  if ($[52] !== t26.Content || $[53] !== t29 || $[54] !== t34 || $[55] !== t35 || $[56] !== t36 || $[57] !== t37 || $[58] !== t38 || $[59] !== t39) {
    t40 = <t26.Content value={t27} className={t28}>{t29}{t34}{t35}{t36}{t37}{t38}{t39}</t26.Content>;
    $[52] = t26.Content;
    $[53] = t29;
    $[54] = t34;
    $[55] = t35;
    $[56] = t36;
    $[57] = t37;
    $[58] = t38;
    $[59] = t39;
    $[60] = t40;
  } else {
    t40 = $[60];
  }
  let t41;
  if ($[61] !== connectedWallet.data || $[62] !== navigation.openConnectedWallet || $[63] !== t) {
    t41 = connectedWallet.data ? <AnimatedButton variant="tertiary" suffix={ArrowRight01Icon} className="h-auto min-h-16.5 w-full justify-between rounded-control bg-background-tertiary p-3" onClick={navigation.openConnectedWallet}><span className="flex items-center gap-3"><OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src="/assets/onramp/method-crypto.png" alt="" size="size-10" className="bg-transparent" /><span className="flex flex-col items-start"><span className="text-body font-medium">{t("connected_wallet_title")}</span><span className="text-caption text-foreground-secondary">{t("connected_wallet_subtitle")}</span></span></span></AnimatedButton> : null;
    $[61] = connectedWallet.data;
    $[62] = navigation.openConnectedWallet;
    $[63] = t;
    $[64] = t41;
  } else {
    t41 = $[64];
  }
  let t42;
  if ($[65] !== cryptoDeposit.data || $[66] !== navigation.openCryptoDeposit || $[67] !== t) {
    t42 = cryptoDeposit.data ? <AnimatedButton variant="tertiary" suffix={ArrowRight01Icon} className="h-auto min-h-16.5 w-full justify-between rounded-control bg-background-tertiary p-3" onClick={navigation.openCryptoDeposit}><span className="flex items-center gap-3"><OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src="/assets/onramp/method-crypto.png" alt="" size="size-10" className="bg-transparent" /><span className="flex flex-col items-start"><span className="text-body font-medium">{t("crypto_deposit_title")}</span><span className="text-caption text-foreground-secondary">{t("crypto_deposit_subtitle")}</span></span></span></AnimatedButton> : null;
    $[65] = cryptoDeposit.data;
    $[66] = navigation.openCryptoDeposit;
    $[67] = t;
    $[68] = t42;
  } else {
    t42 = $[68];
  }
  let t43;
  if ($[69] !== manualEnabled || $[70] !== navigation.openManualTransfer || $[71] !== t) {
    t43 = manualEnabled ? <AnimatedButton variant="tertiary" suffix={ArrowRight01Icon} className="h-auto min-h-16.5 w-full justify-between rounded-control bg-background-tertiary p-3" onClick={navigation.openManualTransfer}><span className="flex items-center gap-3"><OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src="/assets/onramp/method-manual-transfer.png" alt="" size="size-10" className="bg-transparent" /><span className="flex flex-col items-start"><span className="text-body font-medium">{t("manual_transfer")}</span><span className="text-caption text-foreground-secondary">{t("manual_transfer_subtitle")}</span></span></span></AnimatedButton> : null;
    $[69] = manualEnabled;
    $[70] = navigation.openManualTransfer;
    $[71] = t;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== exchanges.data || $[74] !== onExchange || $[75] !== t) {
    t44 = exchanges.data?.includes("coinbase") && onExchange ? <AnimatedButton variant="tertiary" suffix={ArrowRight01Icon} className="h-auto min-h-16.5 w-full justify-between rounded-control bg-background-tertiary p-3" onClick={onExchange}><span className="flex flex-col items-start"><span className="text-body font-medium">{t("exchange_transfer")}</span><span className="text-caption text-foreground-secondary">{t("exchange_transfer_description")}</span></span></AnimatedButton> : null;
    $[73] = exchanges.data;
    $[74] = onExchange;
    $[75] = t;
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  let t45;
  if ($[77] !== connectedWallet.data || $[78] !== cryptoDeposit.data || $[79] !== exchanges.data || $[80] !== manualEnabled || $[81] !== onExchange) {
    t45 = !connectedWallet.data && !cryptoDeposit.data && !manualEnabled && !(exchanges.data?.includes("coinbase") && onExchange) ? <ComingSoon /> : null;
    $[77] = connectedWallet.data;
    $[78] = cryptoDeposit.data;
    $[79] = exchanges.data;
    $[80] = manualEnabled;
    $[81] = onExchange;
    $[82] = t45;
  } else {
    t45 = $[82];
  }
  let t46;
  if ($[83] !== t41 || $[84] !== t42 || $[85] !== t43 || $[86] !== t44 || $[87] !== t45) {
    t46 = <AppTabs.Content value="wallets" className="mt-3 flex flex-col gap-3">{t41}{t42}{t43}{t44}{t45}</AppTabs.Content>;
    $[83] = t41;
    $[84] = t42;
    $[85] = t43;
    $[86] = t44;
    $[87] = t45;
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  let t47;
  if ($[89] !== t22.Root || $[90] !== t24 || $[91] !== t25 || $[92] !== t40 || $[93] !== t46 || $[94] !== tab) {
    t47 = <t22.Root value={tab} activationMode={t23} onValueChange={t24}>{t25}{t40}{t46}</t22.Root>;
    $[89] = t22.Root;
    $[90] = t24;
    $[91] = t25;
    $[92] = t40;
    $[93] = t46;
    $[94] = tab;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== t19 || $[97] !== t21 || $[98] !== t47) {
    t48 = <section className={t18} aria-label={t19}>{t21}{t47}</section>;
    $[96] = t19;
    $[97] = t21;
    $[98] = t47;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  let t49;
  if ($[100] !== t16 || $[101] !== t17 || $[102] !== t48) {
    t49 = <div className={t15}>{t16}{t17}{t48}</div>;
    $[100] = t16;
    $[101] = t17;
    $[102] = t48;
    $[103] = t49;
  } else {
    t49 = $[103];
  }
  return t49;
}
function deferPointerActivation(event: MouseEvent<HTMLButtonElement>): void {
  // Selecting on mouse-down recenters the shorter sheet before mouse-up, turning the click into a backdrop dismissal.
  event.preventDefault();
}
function FundingMethodRow(t0) {
  const $ = _c(8);
  const {
    method,
    subtitle,
    onSelect
  } = t0;
  const labels = useFundingMethodLabels();
  const t1 = labels[method];
  const t2 = `/assets/onramp/method-${fundingMethodArtwork[method]}.png`;
  let t3;
  if ($[0] !== method || $[1] !== onSelect) {
    t3 = onSelect ? {
      onSelect: () => onSelect(method)
    } : {};
    $[0] = method;
    $[1] = onSelect;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== subtitle || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <FundingOptionRow label={t1} subtitle={subtitle} artwork={t2} {...t3} />;
    $[3] = subtitle;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export function FundingOptionRow(t0) {
  const $ = _c(15);
  const {
    label,
    artwork,
    subtitle,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== artwork) {
    t1 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={artwork} alt="" size="size-10" className="bg-transparent" />;
    $[0] = artwork;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-body font-medium">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== subtitle) {
    t3 = <span className="text-caption text-foreground-secondary">{subtitle}</span>;
    $[4] = subtitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="flex flex-col items-start">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <span className="flex items-center gap-3">{t1}{t4}</span>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const content = t5;
  let t6;
  if ($[12] !== content || $[13] !== onSelect) {
    t6 = onSelect ? <AnimatedButton variant="tertiary" suffix={ArrowRight01Icon} className="h-auto min-h-16.5 w-full justify-between rounded-control p-3" onClick={() => onSelect()}>{content}</AnimatedButton> : <div className="flex min-h-16.5 items-center p-3 opacity-60">{content}</div>;
    $[12] = content;
    $[13] = onSelect;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function ComingSoon() {
  const $ = _c(4);
  const t = useTranslations("onramp");
  let t0;
  if ($[0] !== t) {
    t0 = t("coming_soon");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <div className="flex min-h-16.5 w-full items-center rounded-control bg-background-tertiary p-3 text-body text-foreground-secondary">{t0}</div>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}